// src/components/Insights/SessionAiSummaryPanel.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from "../../i18n";
import API_BASE_URL from '../../config/api';

const API_URL = API_BASE_URL;
const API_BASE = `${API_URL}/api`;

// Cambia el proveedor sin tocar código: localStorage.setItem('iaProvider','ollama'|'openai'|'heuristic')
const IA_PROVIDER = (typeof window !== 'undefined' && localStorage.getItem('iaProvider')) || 'groq';

function safeNum(x) {
  const n = typeof x === 'string' ? parseFloat(x) : Number(x);
  return Number.isFinite(n) ? n : null;
}

function makeText(language) {
  const en = String(language || '').toLowerCase().startsWith('en');
  return {
    aiTitle: en ? 'Session AI summary' : 'Resumen IA de la sesión',
    source: en ? 'Source' : 'Fuente',
    copy: en ? 'Copy' : 'Copiar',
    recalc: en ? 'Recalculate' : 'Recalcular',
    generating: en ? 'Generating...' : 'Generando...',
    summaryFallback: en ? 'Session summary' : 'Resumen de la sesión',
    findings: en ? 'Findings' : 'Hallazgos',
    riskFlags: en ? 'Risk flags' : 'Banderas de riesgo',
    next24h: en ? 'Next 24-48h' : 'Proximas 24-48h',
    nextKeySession: en ? 'Next key session' : 'Siguiente sesion clave',
    noUser: en ? 'No userId/athleteId available to generate the summary.' : 'No hay userId/athleteId disponible para generar el resumen.',
    cannotGenerate: en ? 'Could not generate the AI summary' : 'No se pudo generar el resumen IA',
    loadVeryHigh: en ? 'very high' : 'muy alta',
    loadModerateHigh: en ? 'moderate-high' : 'moderada-alta',
    loadModerate: en ? 'moderate' : 'moderada',
    loadLight: en ? 'light' : 'liviana',
    loadUnknown: en ? 'not determined' : 'no determinada',
  };
}

function classifyTsb(tsb, language = 'es') {
  const en = String(language || '').toLowerCase().startsWith('en');
  if (tsb === null || tsb === undefined) return null;
  if (tsb <= -20) return { level: 'high', label: en ? 'high fatigue' : 'fatiga alta', hint: en ? 'prioritize recovery' : 'prioriza recuperación' };
  if (tsb <= -10) return { level: 'med', label: en ? 'moderate fatigue' : 'fatiga moderada', hint: en ? 'avoid intensity' : 'evita intensidad' };
  if (tsb <= 10) return { level: 'good', label: en ? 'optimal zone' : 'zona óptima', hint: en ? 'good window for quality work' : 'buena ventana para calidad' };
  return { level: 'fresh', label: en ? 'fresh' : 'fresco', hint: en ? 'watch excess intensity' : 'cuidado con exceso de intensidad' };
}

function classifyRamp(ramp, language = 'es') {
  const en = String(language || '').toLowerCase().startsWith('en');
  if (ramp === null || ramp === undefined) return null;
  if (ramp > 8) return { level: 'high', label: en ? 'aggressive ramp' : 'ramp agresivo' };
  if (ramp >= 5) return { level: 'med', label: en ? 'moderate ramp' : 'ramp moderado' };
  if (ramp >= 0) return { level: 'low', label: en ? 'smooth ramp' : 'ramp suave' };
  return { level: 'down', label: en ? 'declining load' : 'carga en descenso' };
}

/**
 * Intenta obtener userId de forma robusta (sin romper nada si no existe).
 * - localStorage.userId
 * - localStorage.user => {_id|id}
 * - localStorage.token (JWT) => {userId|id|_id|sub}
 */
function getUserIdSafe() {
  if (typeof window === 'undefined') return null;

  const direct = localStorage.getItem('userId');
  if (direct) return String(direct);

  const rawUser = localStorage.getItem('user');
  if (rawUser) {
    try {
      const u = JSON.parse(rawUser);
      const id = u?._id || u?.id || u?.userId;
      if (id) return String(id);
    } catch (_) {}
  }

  const token = localStorage.getItem('token');
  if (token && token.split('.').length >= 2) {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      const p = JSON.parse(json);
      const id = p?.userId || p?.id || p?._id || p?.sub;
      if (id) return String(id);
    } catch (_) {}
  }

  return null;
}

function parseHmsToMinutes(str) {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim();
  // HH:MM:SS
  if (/^\d{1,3}:\d{1,2}:\d{1,2}$/.test(s)) {
    const parts = s.split(':').map((p) => parseFloat(p));
    if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
    return parts[0] * 60 + parts[1] + parts[2] / 60;
  }
  // MM:SS
  if (/^\d{1,3}:\d{1,2}$/.test(s)) {
    const parts = s.split(':').map((p) => parseFloat(p));
    if (parts.length !== 2 || parts.some((p) => !Number.isFinite(p))) return null;
    return parts[0] + parts[1] / 60;
  }
  return null;
}

/**
 * Normaliza duración a MINUTOS:
 * - si viene durationMin/duracionMin pero en realidad son SEGUNDOS (ej: 7040), lo convierte a min
 * - si viene duration/duracion como HH:MM:SS -> lo convierte a min
 * - si viene durationSeconds/duracionSeg -> lo convierte a min
 */
function getDurationMinutes({ completed = {}, metrics = {} }) {
  // 1) durationMin / duracionMin / metrics.durationMin
  const rawMin = safeNum(completed.durationMin ?? completed.duracionMin ?? metrics.durationMin);
  if (rawMin !== null) {
    // Heurística: > 1000 normalmente son segundos (ej 7040 = 117.33 min)
    if (rawMin > 1000 && rawMin < 300000) return rawMin / 60;
    return rawMin;
  }

  // 2) durationSeconds / duracionSeg / duracionSec
  const rawSec = safeNum(
    completed.durationSeconds ??
      completed.duracionSeg ??
      completed.duracionSec ??
      metrics.durationSeconds ??
      metrics.duracionSeg ??
      metrics.duracionSec
  );
  if (rawSec !== null) return rawSec / 60;

  // 3) duration / duracion como string HMS
  const d = completed.duration ?? completed.duracion ?? metrics.duration ?? metrics.duracion;
  const parsed = parseHmsToMinutes(d);
  if (parsed !== null) return parsed;

  // 4) duration / duracion como número (puede ser segundos o minutos)
  const raw = safeNum(d);
  if (raw !== null) {
    // si es muy grande, asumimos segundos
    if (raw > 1000 && raw < 300000) return raw / 60;
    return raw;
  }

  return null;
}

function deriveHeuristic({ sport, completed = {}, metrics = {}, banister = {}, language = 'es' }) {
  const en = String(language || '').toLowerCase().startsWith('en');
  const copy = makeText(language);
  const tss = safeNum(completed.tss ?? completed.TSS ?? metrics.tss ?? metrics.TSS);
  const durMin = getDurationMinutes({ completed, metrics });

  const ctl = safeNum(banister.ctl);
  const atl = safeNum(banister.atl);
  const tsb = safeNum(banister.tsb);
  const ramp7d = safeNum(banister.ramp7d);

  const tsbC = classifyTsb(tsb, language);
  const rampC = classifyRamp(ramp7d, language);

  const loadLabel =
    tss !== null && tss >= 100
      ? copy.loadVeryHigh
      : tss !== null && tss >= 75
      ? copy.loadModerateHigh
      : tss !== null && tss >= 50
      ? copy.loadModerate
      : tss !== null
      ? copy.loadLight
      : copy.loadUnknown;

  const headlineParts = [];
  if (tss !== null) headlineParts.push(en ? `Load ${loadLabel}` : `Carga ${loadLabel}`);
  if (tsbC) headlineParts.push(`(${tsbC.label})`);
  const headline = headlineParts.join(' ').trim() || copy.summaryFallback;

  const highlights = [];
  if (tss !== null) highlights.push(`TSS: ${Math.round(tss)}`);
  if (durMin !== null) highlights.push(en ? `Duration: ${Math.round(durMin)} min` : `Duración: ${Math.round(durMin)} min`);
  if (ctl !== null) highlights.push(`CTL: ${ctl.toFixed(1)}`);
  if (atl !== null) highlights.push(`ATL: ${atl.toFixed(1)}`);
  if (tsb !== null) highlights.push(`TSB: ${tsb.toFixed(1)}`);
  if (ramp7d !== null) highlights.push(`Ramp 7d: ${ramp7d.toFixed(1)}`);

  const risks = [];
  if (tsb !== null && tsb <= -10) risks.push(en ? 'Elevated fatigue (TSB < -10).' : 'Fatiga elevada (TSB < -10).');
  if (ramp7d !== null && ramp7d > 8) risks.push(en ? 'Aggressive 7-day ramp (> +8).' : 'Ramp 7 días agresivo (> +8).');
  if (tss !== null && tss >= 100) risks.push(en ? 'Very high load today (TSS >= 100).' : 'Carga muy alta hoy (TSS ≥ 100).');

  const next24_48h = [];
  if (tsb !== null && tsb <= -10) {
    next24_48h.push(en ? 'Next 24-48h: rest or a very easy Z1/Z2 session.' : 'Próximas 24–48h: descanso o sesión Z1/Z2 muy suave.');
  } else if (tss !== null && tss >= 75) {
    next24_48h.push(en ? 'Next 24h: active recovery + mobility.' : 'Próximas 24h: recuperación activa + movilidad.');
  } else {
    next24_48h.push(en ? 'Next 24h: light or technical session according to plan.' : 'Próximas 24h: sesión liviana o técnica según plan.');
  }
  next24_48h.push(en ? 'Prioritize sleep, hydration and post-session nutrition.' : 'Prioriza sueño, hidratación y nutrición post-sesión.');

  const nextKeySession =
    tsb !== null && tsb <= -10
      ? (en ? 'Next key session: when TSB rises, ideally above -10.' : 'Próxima sesión clave: cuando el TSB suba (idealmente > -10).')
      : (en ? 'Next key session: you can sustain a quality stimulus if the plan requires it.' : 'Próxima sesión clave: puedes sostener un estímulo de calidad si el plan lo requiere.');

  const summary = [
    sport ? (en ? `Sport: ${sport}.` : `Deporte: ${sport}.`) : null,
    tss !== null && durMin !== null
      ? (en ? `Today you accumulated ~${Math.round(tss)} TSS in ${Math.round(durMin)} min.` : `Hoy sumaste ~${Math.round(tss)} TSS en ${Math.round(durMin)} min.`)
      : tss !== null
      ? (en ? `Today you accumulated ~${Math.round(tss)} TSS.` : `Hoy sumaste ~${Math.round(tss)} TSS.`)
      : durMin !== null
      ? (en ? `Duration ${Math.round(durMin)} min.` : `Duración ${Math.round(durMin)} min.`)
      : null,
    tsbC ? (en ? `Current state: ${tsbC.label} -> ${tsbC.hint}.` : `Estado actual: ${tsbC.label} → ${tsbC.hint}.`) : null,
    rampC ? (en ? `Trend: ${rampC.label}.` : `Tendencia: ${rampC.label}.`) : null,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    headline,
    summary,
    highlights,
    risks,
    next24_48h,
    nextKeySession,
    confidence: 0.55,
    meta: { provider: 'heuristic' },
  };
}

function localizeServerHeuristic(payload, language) {
  const en = String(language || '').toLowerCase().startsWith('en');
  if (!en || !payload || typeof payload !== 'object') return payload;

  const translate = (value) => {
    if (typeof value !== 'string') return value;
    return value
      .replace(/^Resumen de la sesión$/i, 'Session summary')
      .replace(/\bDeporte:/g, 'Sport:')
      .replace(/\bDuración:/g, 'Duration:')
      .replace(/\bCTL:/g, 'CTL:')
      .replace(/\bATL:/g, 'ATL:')
      .replace(/\bTSB:/g, 'TSB:')
      .replace(/\bRamp 7d:/g, 'Ramp 7d:')
      .replace(/Wellness PRE: (\d+)\/25 \(excelente\)/g, 'Wellness PRE: $1/25 (excellent)')
      .replace(/Wellness PRE: (\d+)\/25 \(normal\)/g, 'Wellness PRE: $1/25 (normal)')
      .replace(/Wellness PRE: (\d+)\/25 \(bajo\)/g, 'Wellness PRE: $1/25 (low)')
      .replace(/Wellness POST: (\d+)\/25 \(excelente\)/g, 'Wellness POST: $1/25 (excellent)')
      .replace(/Wellness POST: (\d+)\/25 \(normal\)/g, 'Wellness POST: $1/25 (normal)')
      .replace(/Wellness POST: (\d+)\/25 \(bajo\)/g, 'Wellness POST: $1/25 (low)')
      .replace(/Fatiga crónica alta \(TSB < -20\): reducir carga urgente\./g, 'High chronic fatigue (TSB < -20): reduce load urgently.')
      .replace(/Fatiga moderada \(TSB < -10\): evitar sesiones intensas\./g, 'Moderate fatigue (TSB < -10): avoid intense sessions.')
      .replace(/Incremento semanal agresivo \(Ramp > 8\): riesgo de lesión\./g, 'Aggressive weekly increase (Ramp > 8): injury risk.')
      .replace(/Carga extrema hoy \(TSS ≥ 150\): recuperación prioritaria\./g, 'Extreme load today (TSS ≥ 150): recovery is a priority.')
      .replace(/Carga alta hoy \(TSS ≥ 100\): sesión exigente\./g, 'High load today (TSS ≥ 100): demanding session.')
      .replace(/Wellness PRE bajo \((\d+)\/25\): el atleta llegó en mal estado\./g, 'Low wellness PRE ($1/25): athlete arrived in poor condition.')
      .replace(/Wellness POST bajo \((\d+)\/25\): recuperación comprometida\./g, 'Low wellness POST ($1/25): recovery compromised.')
      .replace(/Daño muscular post-sesión elevado: priorizar recuperación activa\./g, 'Elevated post-session muscle damage: prioritize active recovery.')
      .replace(/Sin banderas de riesgo significativas\./g, 'No significant risk flags.')
      .replace(/Próximas 24–48h: descanso o sesión Z1\/Z2 muy suave\./g, 'Next 24-48h: rest or a very easy Z1/Z2 session.')
      .replace(/Próximas 24-48h: descanso o sesión Z1\/Z2 muy suave\./g, 'Next 24-48h: rest or a very easy Z1/Z2 session.')
      .replace(/Próximas 24–48h:/g, 'Next 24-48h:')
      .replace(/Próximas 24-48h:/g, 'Next 24-48h:')
      .replace(/Próximas 24h: sesión liviana o técnica según plan\./g, 'Next 24h: light or technical session according to plan.')
      .replace(/Próximas 24h: recuperación activa \+ movilidad\./g, 'Next 24h: active recovery + mobility.')
      .replace(/Prioriza sueño, hidratación y nutrición post-sesión\./g, 'Prioritize sleep, hydration and post-session nutrition.')
      .replace(/Próxima sesión clave: puedes sostener un estímulo de calidad si el plan lo requiera\./g, 'Next key session: you can sustain a quality stimulus if the plan requires it.')
      .replace(/Próxima sesión clave: puedes sostener un estímulo de calidad si el plan lo requiere\./g, 'Next key session: you can sustain a quality stimulus if the plan requires it.')
      .replace(/Próxima sesión clave: cuando el TSB suba \(idealmente > -10\)\./g, 'Next key session: when TSB rises, ideally above -10.');
  };

  const translateArray = (items) => (Array.isArray(items) ? items.map(translate) : items);

  return {
    ...payload,
    headline: translate(payload.headline),
    summary: translate(payload.summary),
    highlights: translateArray(payload.highlights),
    risks: translateArray(payload.risks),
    next24_48h: translateArray(payload.next24_48h),
    nextKeySession: translate(payload.nextKeySession),
  };
}

export default function SessionAiSummaryPanel({
  athleteId,
  activityId,
  date,
  sport,
  completed = {},
  metrics = {},
  banister, // opcional
  autoRun = true,
}) {
  const { t, language } = useTranslation();
  const copy = useMemo(() => makeText(language), [language]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [wellness, setWellness] = useState({ pre: null, post: null });
  const [wellnessLoaded, setWellnessLoaded] = useState(false);

  const provider = useMemo(() => IA_PROVIDER, []);
  const query = useMemo(() => `?provider=${encodeURIComponent(provider)}`, [provider]);

  // ✅ fallback inmediato
  const heuristic = useMemo(
    () => deriveHeuristic({ sport, completed, metrics, banister: banister || {}, language }),
    [sport, completed, metrics, banister, language]
  );

  const canCallApi = useMemo(() => true, []);

  // Cargar wellness del atleta para esta actividad
  useEffect(() => {
    if (!athleteId) return;
    if (!date) { setWellnessLoaded(true); return; }
    const d = typeof date === 'string' ? date.split('T')[0] : new Date(date).toISOString().split('T')[0];
    fetch(`${API_BASE}/wellness/${athleteId}?date=${d}`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          const pre = data.find(w => w.timing === 'pre') || null;
          const post = data.find(w => w.timing === 'post') || null;
          setWellness({ pre, post });
        }
        setWellnessLoaded(true);
      }).catch(() => { setWellnessLoaded(true); });
  }, [athleteId, date]);

  const run = async () => {
    setLoading(true);
    setErr('');
    try {
      // ✅ userId real (si existe) o athleteId (fallback) para no romper
      const userIdFromSession = getUserIdSafe();
      const effectiveUserId = (userIdFromSession || athleteId || '').toString().trim();

      if (!effectiveUserId) {
        setData(null);
        setErr(copy.noUser);
        return;
      }

      // ✅ intenta incluir fileId si existe
      const fileId =
        completed?.fileId ||
        completed?.completed?.fileId ||
        metrics?.fileId ||
        completed?.fitFileId ||
        null;

      // ✅ Siempre nuevo (lo pediste)
      const forceNew = true;

      const payload = {
        userId: effectiveUserId,
        language,
        athleteId,
        activityId,
        fileId,
        sport,
        completed,
        metrics,
        banister,
        wellness,
        forceNew,
      };

      const res = await fetch(`${API_BASE}/session/ai-summary${query}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': effectiveUserId,
        },
        body: JSON.stringify(payload),
      });

      const j = await res.json().catch(() => null);
      if (!res.ok) throw new Error(j?.message || j?.error || `HTTP ${res.status}`);

      if (j && typeof j === 'object') setData(j);
      else setData({ headline: copy.aiTitle, summary: String(j || ''), meta: { provider } });
    } catch (e) {
      setErr(e?.message || copy.cannotGenerate);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoRun) return;
    if (!canCallApi) return;
    if (!completed || Object.keys(completed).length === 0) return;
    // Esperar a que wellness cargue antes de disparar
    if (athleteId && date && !wellnessLoaded) return;
    // Esperar a que banister tenga datos reales (no objeto vacio)
    if (athleteId && banister && Object.keys(banister).length === 0) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, canCallApi, activityId, sport, banister?.ctl, banister?.atl, banister?.tsb, wellness, wellnessLoaded]);

  const shownRaw = data || heuristic;
  const shown = useMemo(
    () => localizeServerHeuristic(shownRaw, language),
    [shownRaw, language]
  );
  const providerLabel = shown?.meta?.provider || (data ? provider : 'heuristic');
  const labelOrFallback = (key, fallback) => {
    const value = t(key);
    return value && value !== key ? value : fallback;
  };
  const findingsLabel = labelOrFallback('pmcPane.diagFindings', copy.findings);
  const next24hLabel = labelOrFallback('insights.next24h', copy.next24h);
  const nextKeySessionLabel = labelOrFallback('insights.nextKeySession', copy.nextKeySession);

  const copyToClipboard = async () => {
    try {
      const txt = [
        shown?.headline,
        shown?.summary,
        shown?.highlights?.length ? `• ${shown.highlights.join('\n• ')}` : '',
        shown?.risks?.length ? `${copy.riskFlags}:\n• ${shown.risks.join('\n• ')}` : '',
        shown?.next24_48h?.length ? `${next24hLabel}:\n• ${shown.next24_48h.join('\n• ')}` : '',
        shown?.nextKeySession ? `${nextKeySessionLabel}: ${shown.nextKeySession}` : '',
      ]
        .filter(Boolean)
        .join('\n\n');
      await navigator.clipboard.writeText(txt);
    } catch {
      // nada
    }
  };

  if (!completed || Object.keys(completed).length === 0) return null;

  return (
    <div className="mt-4 p-3" style={{ border: '1px solid #e6e9ef', borderRadius: 10, background: '#fff' }}>
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="m-0" style={{ color: '#193255', fontSize: 14, fontWeight: 700 }}>
          🧠 {copy.aiTitle}
        </h6>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyToClipboard}>
            {copy.copy}
          </button>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={run} disabled={loading}>
            {loading ? copy.generating : copy.recalc}
          </button>
        </div>
      </div>

      <div className="mt-1" style={{ fontSize: 12, opacity: 0.75 }}>
        {copy.source}: <strong>{providerLabel}</strong>
        {err ? <span className="ms-2 text-danger">({err})</span> : null}
      </div>

      <div className="mt-2">
        <div style={{ fontWeight: 700 }}>{shown?.headline || copy.summaryFallback}</div>
        {shown?.summary ? <div className="mt-1">{shown.summary}</div> : null}

        {Array.isArray(shown?.highlights) && shown.highlights.length > 0 && (
          <div className="mt-2">
            <div style={{ fontWeight: 600 }}>{findingsLabel}</div>
            <ul className="mb-0">
              {shown.highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(shown?.risks) && shown.risks.length > 0 && (
          <div className="mt-2">
            <div style={{ fontWeight: 600 }}>{copy.riskFlags}</div>
            <ul className="mb-0">
              {shown.risks.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(shown?.next24_48h) && shown.next24_48h.length > 0 && (
          <div className="mt-2">
            <div style={{ fontWeight: 600 }}>{next24hLabel}</div>
            <ul className="mb-0">
              {shown.next24_48h.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          </div>
        )}

        {shown?.nextKeySession && (
          <div className="mt-2">
            <div style={{ fontWeight: 600 }}>{nextKeySessionLabel}</div>
            <div>{shown.nextKeySession}</div>
          </div>
        )}
      </div>
    </div>
  );
}
